import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import type { Step2ZoneSummary } from '../../components/climb-create/Step2Form';
import type { RoutesetterSelection } from '../../components/routesetters/RoutesetterPicker';
import { climbScansService, ScanValidationError } from '../../services/climb-scans';
import { zonesService } from '../../services/zones';
import { gymsService } from '../../services/gyms';
import { hasScanAnswer, type ClimbScan, type ClimbScanColor, type ScanDetector, type ScanSensitivity, type PhotoArea } from '../../types/climb-scan';
import type { Hold, RouteLine } from '../../types/climb';
import type { BoardHand } from '../../types/device';
import type { Zone } from '../../types/zone';
import { useAuth } from '../../hooks/useAuth';
import { isSuperAdmin } from '../../types/user';
import { useCurrentGym } from '../hooks/useCurrentGym';
import { ScanListColumn } from '../components/scans/ScanListColumn';
import { ScanHoldEditor } from '../components/scans/ScanHoldEditor';
import { ScanLineEditor } from '../components/scans/ScanLineEditor';
import { ScanColorCard } from '../components/scans/ScanColorCard';
import { ScanClimbActions } from '../components/scans/ScanClimbActions';
import { ScanClimbForm, type ScanColorDraft } from '../components/scans/ScanClimbForm';
import { ScanColorPreviews } from '../components/scans/ScanColorPreviews';
import { ScanPlaceholderPicker } from '../components/scans/ScanPlaceholderPicker';
import { previewsForScan } from '../components/scans/scan-color-previews';
import { ScanUploadDialog } from '../components/scans/ScanUploadDialog';
import { RescanDialog } from '../components/scans/RescanDialog';
import { ScanDefaults } from '../components/scans/ScanDefaults';
import { ScanWallTools } from '../components/scans/ScanWallTools';
import { useScanWallTools } from '../components/scans/useScanWallTools';
import { ScanEditorLayout } from '../components/scans/ScanEditorLayout';
import { ColumnResizer } from '../components/scans/ColumnResizer';
import {
  holdsFromDetections,
  isHidden,
  isMaybe,
  untakenPlaceholders as untakenOf,
} from '../components/scans/detected-holds';
import {
  missingPhotoHoldRoles,
  photoHoldRole,
} from '../../components/climbs/photo-hold-roles';
import {
  assignPhotoHands,
  clearPhotoHands,
  orderPhotoHolds,
  photoHandsOf,
  startingPhotoHand,
} from '../../components/climbs/photo-circuit';
import CircuitList from '../../components/climbs/CircuitList';
import { usePhotoHolds } from '../../components/climbs/photo-editor/usePhotoHolds';
import { useClearSelectionOutside } from '../../components/climbs/climb-editor/useClearSelectionOutside';
import { usePhotoUpload } from '../../components/climbs/climb-editor/usePhotoUpload';
import { CropChoiceDialog } from '../../components/climbs/climb-editor/CropChoiceDialog';
import {
  WHOLE_PHOTO,
  cropHolds,
  cropLine,
  cropStoredImage,
  isCrop,
  visibleRect,
  type CropRect,
} from '../../components/climbs/climb-editor/crop';
import { isDrawableLine } from '../../components/climbs/route-line';
import { climbToParams, readScanSelection, scanToParams } from '../components/scans/scan-url';
import type { ScanEditorChrome } from '../components/scans/ScanEditorLayout';
import {
  CLIMBS_WIDTH_STORAGE_KEY,
  DEFAULT_CLIMBS_WIDTH,
  DEFAULT_PANEL_WIDTH,
  MAX_CLIMBS_WIDTH,
  MAX_PANEL_WIDTH,
  MIN_CLIMBS_WIDTH,
  MIN_PANEL_WIDTH,
  PANEL_WIDTH_STORAGE_KEY,
  useStoredWidth,
} from '../components/scans/column-width';

/** How often an unfinished scan is re-read while the analysis is still running. */
const POLL_INTERVAL_MS = 5000;

/**
 * How many of those reads a re-analysed colour is given before the page stops
 * waiting. A colour has no status to go by, so without a limit a job that died
 * would leave the page polling for the rest of the session.
 */
const RESCAN_POLL_ATTEMPTS = 24;

/** The marks drawn on the photo for one climb, kept beside its form fields. */
interface ScanColorMarks {
  holds: Hold[];
  line: RouteLine | null;
  /**
   * Whether those holds are a **sequence** rather than a set: the climb is
   * climbed hold by hold in the order they are stored in. Kept with the marks and
   * not with the form's fields because it is a fact about the drawing — it is
   * what puts a number on every circle, and what the fourth column lists.
   */
  circuit: boolean;
  /** And whether that sequence says which hand takes each hold. */
  forcedHands: boolean;
}

/**
 * What a colour with nothing drawn on it yet hands the editor. One array for all
 * of them, because a fresh `[]` every render is a fresh identity every render.
 */
const NO_HOLDS: Hold[] = [];

/** Every field a form reads, so nothing downstream meets an absent list. */
const EMPTY_DRAFT: ScanColorDraft = {
  name: '',
  gradeId: '',
  colorName: '',
  routeSetter: null,
  exceptions: [],
  description: '',
};

/** And a whole colour nobody has touched: nothing drawn, and no circuit. */
const NO_MARKS: ScanColorMarks = { holds: NO_HOLDS, line: null, circuit: false, forcedHands: false };

function draftFromColor(color: ClimbScanColor, scan: ClimbScan): ScanColorDraft {
  const setter = color.climb?.route_setter ?? null;
  // A colour that has not been worked yet inherits the scan's own answers:
  // whoever set this wall, and the colour the analysis read off the holds.
  const fallbackSetter = scan.route_setters[0] ?? null;

  return {
    name: color.climb?.name ?? color.climb_name ?? '',
    gradeId: color.climb?.grade_id ?? '',
    colorName: color.climb?.hold_color ?? color.color_name ?? '',
    routeSetter: setter ?? fallbackSetter,
    exceptions: color.climb?.climb_exceptions ?? [],
    description: color.climb?.description ?? '',
  };
}

/**
 * The prefix that tells a climb the detector is *offering* from one somebody
 * saved.
 *
 * **A proposal is not a row.** The scan carries the wall's holds and the groups
 * the detector would make of them; the panel draws those groups as climbs so
 * the setter can see what is on offer, and nothing reaches the database until
 * one is saved. Giving them an id of this shape lets every form, draft and set
 * of marks on this page go on being keyed by colour id, which is what keeps the
 * change to a handful of lines instead of a rewrite.
 */
const PROPOSAL_PREFIX = 'proposal:';

function isProposal(colorId: string): boolean {
  return colorId.startsWith(PROPOSAL_PREFIX);
}

/**
 * The detector's suggestions, shaped like the rows they are not.
 *
 * **A maybe is never pre-selected** (`isMaybe`): it stays on the wall, dashed,
 * for the setter to take with a tap, so a proposal opens with only the holds
 * the detector was sure of. A group of nothing but maybes is not offered; the
 * ids keep the group's own place, which is what they are read back by. Nor is
 * a hold the setter hid with the confidence slider (`isHidden`) or excluded by
 * hand (`excluded_holds`).
 */
function proposedColors(scan: ClimbScan): ClimbScanColor[] {
  const excluded = new Set(scan.excluded_holds ?? []);

  return scan.proposed_groups.flatMap((group, index) => {
    const holds = group.holds
      .filter((holdIndex) => !excluded.has(holdIndex))
      .map((holdIndex) => scan.detected_holds[holdIndex])
      .filter((hold): hold is NonNullable<typeof hold> => Boolean(hold))
      .filter((hold) => !isMaybe(hold) && !isHidden(hold, scan.min_score));
    if (holds.length === 0) return [];

    return [
      {
        id: `${PROPOSAL_PREFIX}${index}`,
        climb_scan_id: scan.id,
        color_name: group.name,
        hex_colors: group.hex_colors,
        hold_count: holds.length,
        detected_holds: holds,
        climb_name: group.climb_name ?? null,
        status: 'pending',
        climb: null,
        created_at: scan.created_at,
        updated_at: scan.updated_at,
      },
    ];
  });
}

function marksFromColor(color: ClimbScanColor): ScanColorMarks {
  return {
    holds: color.climb?.holds ?? [],
    line: color.climb?.line ?? null,
    circuit: !!color.climb?.circuit,
    forcedHands: !!color.climb?.forced_hands,
  };
}

/**
 * The gym's wall scans, and the climbs made out of them.
 *
 * Three columns, left to right in the order the work is done: which scan, what
 * it looks like and how its climbs are marked on it, and the list of climbs the
 * analysis found by colour. Selecting one in the third column is what puts its
 * marks on the photo in the second — a scan holds several climbs on one wall, so
 * only the one being worked on is drawn.
 */
export default function ScansPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { slug, loading: gymLoading } = useCurrentGym();
  // Every press of Scan costs a model call and can replace a wall's detections
  // wholesale, so it is a platform admin's to spend — not the gym's own
  // staff's, however senior, and that holds for the first look as much as for
  // another one. A gym's staff photograph the wall and
  // ScanPendingClimbScansJob looks at it; the button is the escape hatch for
  // somebody who knows what a cold boot costs. Mirrors the `authorize_scan!`
  // gate; the server is what enforces it, this only keeps a dead button off the
  // panel.
  const { user } = useAuth();
  const canRescan = isSuperAdmin(user);

  // What is being looked at lives in the URL, so the page can be pasted to a
  // colleague and a reload comes back to the same wall and the same climb.
  const [searchParams, setSearchParams] = useSearchParams();
  const { scanId: selectedScanId, colorId: selectedColorId } = readScanSelection(searchParams);

  const selectScan = useCallback(
    (scanId: string) => setSearchParams(scanToParams(scanId, searchParams)),
    [searchParams, setSearchParams]
  );

  const selectColor = useCallback(
    (colorId: string | null) =>
      // Closing one is not a place to come back to, so it rewrites rather than
      // stacks; opening one is, so it does not.
      setSearchParams(climbToParams(colorId, searchParams), { replace: colorId === null }),
    [searchParams, setSearchParams]
  );

  const [scans, setScans] = useState<ClimbScan[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [scan, setScan] = useState<ClimbScan | null>(null);
  const [scanLoading, setScanLoading] = useState(false);

  const [zones, setZones] = useState<Zone[]>([]);
  const [savingDefaults, setSavingDefaults] = useState(false);

  const [drafts, setDrafts] = useState<Record<string, ScanColorDraft>>({});
  const [marks, setMarks] = useState<Record<string, ScanColorMarks>>({});
  const [savingColorId, setSavingColorId] = useState<string | null>(null);
  // Climbs the setter has gathered off the wall but not yet saved, and the
  // detector's own suggestions they have turned away. Neither is in the
  // database: a scan proposes, and a row means somebody accepted.
  const [customProposals, setCustomProposals] = useState<ClimbScanColor[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [deletingColorId, setDeletingColorId] = useState<string | null>(null);
  // What the rescan dialog is open for. The scan is named explicitly because
  // the list offers it on every row, not only on the one that is open. Null
  // while the dialog is shut.
  const [rescanTarget, setRescanTarget] = useState<{ scanId: string; colorId: string | null } | null>(
    null
  );
  const [rescanning, setRescanning] = useState(false);
  // A colour whose re-analysis is running. A colour has no status of its own —
  // the job just rewrites its boxes — so what is watched for is the record
  // changing, against the timestamp it carried when the job was queued.
  const [awaitedColor, setAwaitedColor] = useState<{ id: string; since: string } | null>(null);
  const [colorErrors, setColorErrors] = useState<Record<string, Record<string, string>>>({});

  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  // How big the picture on screen actually is, and which picture that was: a
  // colour whose photo was cropped on the way up is drawn on that crop, so the
  // measurement has to be thrown away when the picture changes under it.
  const [photoSize, setPhotoSize] = useState<{ url: string; width: number; height: number } | null>(
    null
  );
  // What the setter framed, held while they answer whether that framing is the
  // climb. Null while nothing is being asked.
  const [pendingCrop, setPendingCrop] = useState<{ colorId: string; rect: CropRect } | null>(null);
  const { upload } = usePhotoUpload();
  // Both sized columns are the page's, because the page owns the grid they are
  // columns of. The photo takes whatever the two of them leave.
  const [climbsWidth, setClimbsWidth] = useStoredWidth(
    CLIMBS_WIDTH_STORAGE_KEY,
    DEFAULT_CLIMBS_WIDTH,
    MIN_CLIMBS_WIDTH,
    MAX_CLIMBS_WIDTH
  );
  const [panelWidth, setPanelWidth] = useStoredWidth(
    PANEL_WIDTH_STORAGE_KEY,
    DEFAULT_PANEL_WIDTH,
    MIN_PANEL_WIDTH,
    MAX_PANEL_WIDTH
  );

  // Which climbs have already been given the analysis's circles. Seeding is a
  // starting point, not a rule: once it has happened the marks are the setter's,
  // so clearing them all must not hand the detection straight back.
  const seededColorIdsRef = useRef<Set<string>>(new Set());

  // Reporting a failure is never something this page renders from, only
  // something it does — so `t` and `toast` ride in a ref instead of in the
  // loaders' dependencies. Depending on them would rebuild a loader whenever
  // either changed identity, and a loader in an effect that rebuilds on every
  // render is an infinite loop rather than a stale closure.
  const notifyRef = useRef({ t, toast });
  notifyRef.current = { t, toast };

  const reportLoadFailure = useCallback(() => {
    const { t: translate, toast: notify } = notifyRef.current;
    notify({ title: translate('gymAdmin.scans.loadError'), variant: 'destructive' });
  }, []);

  const loadScans = useCallback(async () => {
    if (!slug) return;
    try {
      const data = await climbScansService.getScans(slug);
      setScans(data);
    } catch {
      reportLoadFailure();
    } finally {
      setListLoading(false);
    }
  }, [slug, reportLoadFailure]);

  useEffect(() => {
    setListLoading(true);
    loadScans();
  }, [loadScans]);

  // A bare /scans lands on the newest one — the page is never usefully empty.
  // It rewrites rather than stacks: nobody navigated here, so going back should
  // leave the page rather than return to the emptiness it opened on.
  useEffect(() => {
    if (selectedScanId || scans.length === 0) return;

    setSearchParams(scanToParams(scans[0].id, searchParams), { replace: true });
  }, [selectedScanId, scans, searchParams, setSearchParams]);

  useEffect(() => {
    if (!slug) return;
    zonesService.getZonesForLocation(slug, 'gym').then(setZones).catch(() => setZones([]));
    gymsService
      .getGymBySlug(slug)
      .then((gym) =>
        setNameSuggestions(
          (gym.climb_name_suggestions ?? '').split('\n').map((line) => line.trim()).filter(Boolean)
        )
      )
      .catch(() => setNameSuggestions([]));
  }, [slug]);

  // The selected scan in full: the list endpoint carries its colours, but a
  // fresh read is what picks up an analysis that has just landed.
  const loadScan = useCallback(
    async (scanId: string) => {
      if (!slug) return null;
      try {
        const data = await climbScansService.getScan(slug, scanId);
        setScan(data);
        return data;
      } catch {
        reportLoadFailure();
        return null;
      }
    },
    [slug, reportLoadFailure]
  );

  useEffect(() => {
    if (!selectedScanId) {
      setScan(null);
      return;
    }

    let cancelled = false;
    setScanLoading(true);
    loadScan(selectedScanId).then((data) => {
      if (cancelled || !data) return;
      // Reset the working state to what the server says: switching scans must
      // not carry another wall's unsaved edits — or its seeding — across.
      // **The proposals are seeded alongside the rows.** They are climbs on
      // this page like any other, and a form reading a draft that was never
      // put here would start its first edit from nothing.
      setDrafts(Object.fromEntries(
        [ ...proposedColors(data), ...data.colors ].map((color) => [ color.id, draftFromColor(color, data) ])
      ));
      setMarks(Object.fromEntries(data.colors.map((color) => [color.id, marksFromColor(color)])));
      seededColorIdsRef.current = new Set();
      setColorErrors({});
    }).finally(() => {
      if (!cancelled) setScanLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedScanId, loadScan]);

  // Keep re-reading while the analysis runs; it lands on its own schedule.
  const pollingScanId = scan && (scan.status === 'pending' || scan.status === 'processing') ? scan.id : null;
  const loadScanRef = useRef(loadScan);
  loadScanRef.current = loadScan;

  useEffect(() => {
    if (!pollingScanId) return;

    const timer = setInterval(async () => {
      const data = await loadScanRef.current(pollingScanId);
      if (!data || data.status === 'pending' || data.status === 'processing') return;

      // The colours only exist once it is ready, so they are seeded here.
      // **The proposals are seeded alongside the rows.** They are climbs on
      // this page like any other, and a form reading a draft that was never
      // put here would start its first edit from nothing.
      setDrafts(Object.fromEntries(
        [ ...proposedColors(data), ...data.colors ].map((color) => [ color.id, draftFromColor(color, data) ])
      ));
      setMarks(Object.fromEntries(data.colors.map((color) => [color.id, marksFromColor(color)])));
      loadScans();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [pollingScanId, loadScans]);

  /**
   * What the second column lists: the climbs the detector is **offering**, and
   * the ones somebody has actually saved. The offers come first because they
   * are the work still to do.
   */
  const allColors = useMemo(
    () =>
      scan
        ? [ ...proposedColors(scan), ...customProposals ]
            .filter((color) => !dismissed.includes(color.id))
            .concat(scan.colors)
        : [],
    [scan, customProposals, dismissed]
  );

  const selectedColor = useMemo(
    () => allColors.find((color) => color.id === selectedColorId) ?? null,
    [allColors, selectedColorId]
  );

  /**
   * Which wall the dialog is asking about — **the row that was pressed**, not
   * the one on screen. Every row offers the press, including rows for scans
   * nobody has opened, and what the dialog needs from it is per-wall: whether
   * it has ever answered, and which detector would answer next.
   */
  const rescanScan = useMemo(() => {
    if (!rescanTarget) return null;
    if (rescanTarget.scanId === scan?.id) return scan;

    return scans.find((candidate) => candidate.id === rescanTarget.scanId) ?? null;
  }, [rescanTarget, scan, scans]);

  // The picture being drawn on: the wall, or — for a colour saved as a crop of
  // it — that crop. A climb's marks are fractions of the picture it was saved
  // against, so this is the one picture they can be drawn back on.
  const photoUrl = selectedColor?.climb?.cropped_image_url ?? scan?.image_url ?? null;
  // Measured against the picture it was measured on, or not at all: everything
  // drawn is expressed against the natural size, and last picture's is a wrong
  // answer rather than a missing one.
  const naturalSize = photoSize && photoSize.url === photoUrl ? photoSize : null;

  // Watch a colour being re-analysed until its detection changes, then put the
  // new circles on the photo. The setter asked for this colour to be looked at
  // again, so the answer replaces whatever was drawn from the last one — that
  // is the whole point of having asked.
  useEffect(() => {
    if (!slug || !awaitedColor || !naturalSize) return;

    let cancelled = false;
    // Long enough for the model, short enough to give up rather than poll for
    // ever if the job died.
    let attemptsLeft = RESCAN_POLL_ATTEMPTS;

    const timer = setInterval(async () => {
      if (cancelled) return;
      attemptsLeft -= 1;

      const fresh = await climbScansService
        .getColor(slug, awaitedColor.id)
        .catch(() => null);

      if (cancelled) return;
      if (!fresh || fresh.updated_at === awaitedColor.since) {
        if (attemptsLeft <= 0) setAwaitedColor(null);
        return;
      }

      setScan((prev) =>
        prev ? { ...prev, colors: prev.colors.map((c) => (c.id === fresh.id ? fresh : c)) } : prev
      );
      setMarks((prev) => ({
        ...prev,
        [fresh.id]: {
          ...(prev[fresh.id] ?? NO_MARKS),
          holds: holdsFromDetections(fresh.detected_holds, naturalSize.width, naturalSize.height),
        },
      }));
      seededColorIdsRef.current.add(fresh.id);
      setAwaitedColor(null);
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [slug, awaitedColor, naturalSize]);

  // The analysis's detections are the marks a climb starts from, so opening one
  // that has never been worked draws them instead of a bare photo. It waits for
  // the photo's natural size, which is what a mark is measured against.
  useEffect(() => {
    if (!scan || !selectedColor || !naturalSize) return;
    if (scan.climb_type === 'route') return;
    if (seededColorIdsRef.current.has(selectedColor.id)) return;

    seededColorIdsRef.current.add(selectedColor.id);

    // Marks already saved on the climb win over the detection — drawing both
    // would stack two circles on every hold.
    if ((selectedColor.climb?.holds?.length ?? 0) > 0) return;

    const seeded = holdsFromDetections(
      selectedColor.detected_holds,
      naturalSize.width,
      naturalSize.height
    );
    if (seeded.length === 0) return;

    setMarks((prev) => {
      const current = prev[selectedColor.id];
      if (current && current.holds.length > 0) return prev;

      return { ...prev, [selectedColor.id]: { ...(current ?? NO_MARKS), holds: seeded } };
    });
  }, [scan, selectedColor, naturalSize]);

  const gradingSystem = scan?.climb_type === 'route'
    ? scan?.location?.route_grading_system ?? null
    : scan?.location?.boulder_grading_system ?? null;

  const selectedMarks = selectedColorId ? marks[selectedColorId] : undefined;

  /**
   * The draft a patch is applied to when none has been put in state yet.
   *
   * **A patch must never be able to build a partial draft.** Spreading an
   * absent one leaves the form holding only the field that was just changed,
   * and the first thing to read `exceptions` or any other list off it crashes
   * the page — which is exactly what happened the moment proposals started
   * appearing in this list without being seeded.
   */
  function draftFor(colorId: string): ScanColorDraft {
    const color = allColors.find((candidate) => candidate.id === colorId);

    return color && scan ? draftFromColor(color, scan) : EMPTY_DRAFT;
  }

  function patchDraft(colorId: string, patch: Partial<ScanColorDraft>) {
    setDrafts((prev) => ({ ...prev, [colorId]: { ...(prev[colorId] ?? draftFor(colorId)), ...patch } }));
    setColorErrors((prev) => {
      if (!prev[colorId]) return prev;
      const next = { ...prev };
      delete next[colorId];
      return next;
    });
  }

  function patchMarks(colorId: string, patch: Partial<ScanColorMarks>) {
    setMarks((prev) => {
      const current = prev[colorId] ?? NO_MARKS;
      return { ...prev, [colorId]: { ...current, ...patch } };
    });
  }

  const setHolds = useCallback(
    (holds: Hold[]) => {
      if (selectedColorId) patchMarks(selectedColorId, { holds });
    },
    [selectedColorId]
  );

  const setLine = useCallback(
    (line: RouteLine | null) => {
      if (selectedColorId) patchMarks(selectedColorId, { line });
    },
    [selectedColorId]
  );

  const circuit = selectedMarks?.circuit ?? false;
  const forcedHands = circuit && (selectedMarks?.forcedHands ?? false);

  /**
   * The circuit as the fourth column reads it back: the order the circles are in,
   * with the hands it forces written onto them.
   *
   * The hands are worked out again here rather than trusted, because a circle
   * drawn or moved on the photo changes the run without knowing anything about
   * them — the same reason the server works them out again on the way in (see
   * Boulder#assign_photo_hands).
   */
  const circuitHolds = forcedHands
    ? assignPhotoHands(selectedMarks?.holds ?? NO_HOLDS, startingPhotoHand(selectedMarks?.holds ?? NO_HOLDS))
    : (selectedMarks?.holds ?? NO_HOLDS);

  /**
   * Make this climb a circuit, or stop it being one.
   *
   * Switching it on settles the order — a climb is climbed from its starts to its
   * top — and switching it off takes the hands with it, since they alternate down
   * a sequence and a set of circles has none. The server drops them the same way.
   */
  const setCircuit = useCallback(
    (on: boolean) => {
      if (!selectedColorId) return;
      setMarks((prev) => {
        const current = prev[selectedColorId] ?? NO_MARKS;

        return {
          ...prev,
          [selectedColorId]: {
            ...current,
            circuit: on,
            forcedHands: on && current.forcedHands,
            // Off a circuit the hands go with it: they alternate down a sequence,
            // and a set of circles has none.
            holds: on ? orderPhotoHolds(current.holds) : clearPhotoHands(current.holds),
          },
        };
      });
    },
    [selectedColorId]
  );

  /**
   * Whether the circuit says which hand takes each hold.
   *
   * Switching it on settles the order first — hands are read off the climbed
   * order — and writes the alternation onto the holds, so the panel can read it
   * back off them the way the server does.
   */
  const setForcedHands = useCallback(
    (on: boolean) => {
      if (!selectedColorId) return;
      setMarks((prev) => {
        const current = prev[selectedColorId] ?? NO_MARKS;
        const ordered = orderPhotoHolds(current.holds);

        return {
          ...prev,
          [selectedColorId]: {
            ...current,
            forcedHands: on,
            holds: on
              ? assignPhotoHands(ordered, startingPhotoHand(ordered))
              : clearPhotoHands(ordered),
          },
        };
      });
    },
    [selectedColorId]
  );

  /**
   * The hand the circuit's first move is made with — the setter's one say in the
   * alternation, which the rest of the run follows from.
   *
   * Written onto the holds rather than kept beside them: that is where the server
   * keeps it too (see Boulder#starting_hand), so a colour reopened later starts
   * from the same hand it was left on.
   */
  const setStartingHand = useCallback(
    (hand: BoardHand) => {
      if (!selectedColorId) return;
      setMarks((prev) => {
        const current = prev[selectedColorId] ?? NO_MARKS;

        return {
          ...prev,
          [selectedColorId]: { ...current, holds: assignPhotoHands(current.holds, hand) },
        };
      });
    },
    [selectedColorId]
  );

  /**
   * The circles, and every gesture that puts one on the wall.
   *
   * It lives here rather than in `ScanHoldEditor` because the page needs it at
   * save time: the zoom is what says which part of the photo is on screen, and
   * that is the crop. The member app's editor holds its own for the same reason
   * — see PhotoClimbEditor.
   */
  /**
   * Every hold the scan found, as one pool of placeholders.
   *
   * **Across the colours rather than within one.** The grouping is the
   * detector's opinion about which plastic goes together, and a useful one —
   * but the wall is one wall, and a climb is a handful of holds chosen off it.
   * So the picker is given all of them, and the colours stay a shortcut for
   * taking a circuit in one gesture rather than a wall divided into boxes.
   */
  /**
   * **What is done to the wall itself** — the confidence slider, excluding by
   * hand, the colour-wand, an area looked at again — shared with the board
   * panel (useScanWallTools). A hold of a new colour an area adds is a new
   * proposal, and a proposal needs a draft; the ones already being worked on
   * keep theirs.
   */
  const wallApi = useMemo(
    () => ({
      update: (scanId: string, attrs: { min_score?: number | null; excluded_holds?: number[] }) =>
        climbScansService.updateScan(slug ?? '', scanId, attrs),
      addHold: (scanId: string, outline: Array<[number, number]>) =>
        climbScansService.addHold(slug ?? '', scanId, outline),
      scanArea: (scanId: string, area: PhotoArea) => climbScansService.scanArea(slug ?? '', scanId, area),
      reload: (scanId: string) => loadScanRef.current(scanId),
    }),
    [slug]
  );
  const wallTools = useScanWallTools({
    scan,
    setScan,
    api: wallApi,
    naturalSize,
    resetKey: selectedColorId,
    onAreaSettled: (data) =>
      setDrafts((prev) => ({
        ...Object.fromEntries(proposedColors(data).map((color) => [color.id, draftFromColor(color, data)])),
        ...prev,
      })),
  });
  const { shown, placeholders } = wallTools;


  /**
   * The wall's holds this climb has not taken, which stay pressable while it is
   * being edited — matched by where they sit (see `untakenPlaceholders`).
   */
  const untakenPlaceholders = useMemo(
    () => untakenOf(placeholders, selectedMarks?.holds ?? NO_HOLDS),
    [placeholders, selectedMarks]
  );

  /** A placeholder pressed while a climb is open joins that climb. */
  function takeCandidate(hold: Hold) {
    if (!selectedColorId) return;

    setMarks((prev) => {
      const current = prev[selectedColorId] ?? NO_MARKS;

      return { ...prev, [selectedColorId]: { ...current, holds: [ ...current.holds, hold ] } };
    });
  }

  const holdEditor = usePhotoHolds({
    naturalWidth: naturalSize?.width ?? 1,
    naturalHeight: naturalSize?.height ?? 1,
    holds: selectedMarks?.holds ?? NO_HOLDS,
    onChange: setHolds,
    // A press on the circle already chosen walks its **role**: a scan's analysis
    // gets the wall's colour wrong far more often than it gets a hold's size
    // wrong, and correcting that is most of the work on this page.
    repeatPressCycles: 'role',
    // On a circuit every change settles the order the holds are climbed in, and
    // the circles wear their number as they are corrected.
    circuit,
    // **Nothing is drawn on a scanned wall.** Every hold on it is already a
    // placeholder the detector found, so the climb is made by choosing them —
    // a press on bare wall is a press on wall, not a new circle.
    drawing: false,
    // **And the rest of the wall is still there while a climb is open.** A
    // climb is a choice off the wall, and changing one's mind about which
    // holds it is must not mean starting again: the placeholders this climb
    // has not taken stay pressable, and pressing one adds it.
    candidates: untakenPlaceholders,
    onTakeCandidate: takeCandidate,
  });

  // Pressing away from the picture puts the circle down: the setter has moved on
  // to the grade or the setter's name, and the panel over the wall has nothing
  // left to act on.
  useClearSelectionOutside(holdEditor.clearSelection);

  // Every climb on the wall is drawn from scratch: nothing in hand, the start
  // armed again, and an undo that cannot reach back into the colour before.
  const resetHoldsRef = useRef(holdEditor.reset);
  resetHoldsRef.current = holdEditor.reset;
  useEffect(() => {
    resetHoldsRef.current();
  }, [selectedColorId]);

  /** A name from the gym's list that no other climb on this scan is using. */
  function drawName(colorId: string) {
    const taken = new Set(
      Object.entries(drafts)
        .filter(([id]) => id !== colorId)
        .map(([, draft]) => draft.name)
        .filter(Boolean)
    );
    const available = nameSuggestions.filter((name) => !taken.has(name) && name !== drafts[colorId]?.name);
    if (available.length === 0) return;

    patchDraft(colorId, { name: available[Math.floor(Math.random() * available.length)] });
  }

  async function handleSaveDefaults(zone: Step2ZoneSummary | null, setters: RoutesetterSelection[]) {
    if (!slug || !scan) return;
    setSavingDefaults(true);
    try {
      const updated = await climbScansService.updateScan(slug, scan.id, {
        zone_id: zone?.id ?? null,
        route_setter_ids: setters.map((setter) => setter.id),
      });
      setScan(updated);
      toast({ title: t('gymAdmin.scans.defaultsSaved') });
    } catch {
      toast({ title: t('gymAdmin.scans.saveError'), variant: 'destructive' });
    } finally {
      setSavingDefaults(false);
    }
  }

  /**
   * The save, asking first where the photo has been zoomed in on.
   *
   * A setter who zoomed to place a circle precisely did not mean to crop, and
   * one who framed the climb did — the same gesture, two intentions, so the only
   * honest thing is to ask which it was. A route is never asked: its line is
   * drawn by an editor of its own, whose zoom the page cannot see.
   */
  function requestSaveColor(color: ClimbScanColor) {
    const rect = scan?.climb_type === 'route' ? WHOLE_PHOTO : visibleRect(holdEditor.zoom);
    if (photoUrl && naturalSize && isCrop(rect)) {
      return setPendingCrop({ colorId: color.id, rect });
    }

    void handleSaveColor(color, WHOLE_PHOTO);
  }

  /**
   * Put the climb up.
   *
   * `rect` is how much of the photo to keep: the whole of it, or what was on
   * screen. The cut is made here rather than as the picture is zoomed, because
   * until the save nobody has decided that the framing is the climb — and every
   * mark is a fraction of the picture, so cutting it moves all of them (see
   * crop.ts). The wall the crop came out of is kept as the climb's original by
   * the endpoint, which already holds it: see the publish action.
   */
  async function handleSaveColor(color: ClimbScanColor, rect: CropRect) {
    if (!slug || !scan) return;
    const draft = drafts[color.id];
    if (!draft) return;

    setPendingCrop(null);
    setSavingColorId(color.id);
    try {
      const colorMarks = marks[color.id] ?? NO_MARKS;
      let holds = colorMarks.holds;
      let line = colorMarks.line;
      // Set only where a cut was made, so it doubles as "this was cropped".
      let imageSignedId: string | undefined;

      if (photoUrl && naturalSize && isCrop(rect)) {
        const cut = await cropStoredImage(photoUrl, rect, naturalSize.width, naturalSize.height);
        holds = cropHolds(holds, rect, cut.width, cut.height);
        line = cropLine(line, rect, naturalSize.width, naturalSize.height, cut.width, cut.height);
        imageSignedId = await upload(cut.file);
      }

      // The hands go on last, over whatever survived the crop: a circle outside
      // the framing is gone from the climb, and the alternation follows the run
      // that is left rather than the one that was drawn.
      const handed = colorMarks.circuit && colorMarks.forcedHands;
      holds = handed ? assignPhotoHands(holds, startingPhotoHand(holds)) : clearPhotoHands(holds);

      // **A proposal becomes a row here and nowhere else.** The detector's
      // suggestions live on the scan and are drawn in the panel without being
      // written down; saving one is the single call that creates the colour and
      // the climb together, so an abandoned attempt leaves nothing behind.
      const save = isProposal(color.id)
        ? (data: Parameters<typeof climbScansService.saveScanClimb>[3]) =>
            climbScansService.publishScanClimb(slug, scan.id, {
              ...data,
              // The shapes that were chosen, so re-opening the climb draws the
              // same outlines rather than deriving them again.
              detected_holds: color.detected_holds,
            })
        : (data: Parameters<typeof climbScansService.saveScanClimb>[3]) =>
            climbScansService.saveScanClimb(slug, scan.id, color.id, data);

      const saved = await save({
        name: draft.name.trim(),
        grade_id: draft.gradeId,
        zone_id: scan.zone?.id ?? null,
        route_setter_id: draft.routeSetter ? String(draft.routeSetter.id) : null,
        color_name: draft.colorName || null,
        description: draft.description || undefined,
        climb_exceptions: draft.exceptions,
        holds,
        line,
        // A route is one line and has no circuit: the switch is not even offered
        // there, so the flags are only sent for a wall of boulders.
        ...(scan.climb_type === 'route'
          ? {}
          : { circuit: colorMarks.circuit, forced_hands: handed }),
        image_signed_id: imageSignedId,
      });

      // The picture underneath has just become the crop, and the marks that come
      // back are fractions of it. Everything that described the old framing goes
      // with it: the enlargement it was framed at, and an undo stack whose
      // circles are in the wall's coordinates and would land anywhere on this.
      if (imageSignedId) {
        holdEditor.zoom.reset();
        holdEditor.reset();
      }

      // A proposal has just stopped being one: it is appended as a row rather
      // than swapped in place, and the selection moves to the row so the form
      // the setter is looking at is the saved climb's.
      setScan((prev) => {
        if (!prev) return prev;
        if (isProposal(color.id)) return { ...prev, colors: [ ...prev.colors, saved ] };

        return { ...prev, colors: prev.colors.map((c) => (c.id === color.id ? saved : c)) };
      });
      setDrafts((prev) => ({ ...prev, [saved.id]: draftFromColor(saved, scan) }));
      setMarks((prev) => ({ ...prev, [saved.id]: marksFromColor(saved) }));
      if (isProposal(color.id)) selectColor(saved.id);
      toast({ title: t('gymAdmin.scans.climbSaved') });
      loadScans();
    } catch (error) {
      if (error instanceof ScanValidationError) {
        setColorErrors((prev) => ({ ...prev, [color.id]: error.fieldErrors }));
      } else {
        toast({ title: t('gymAdmin.scans.saveError'), variant: 'destructive' });
      }
    } finally {
      setSavingColorId(null);
    }
  }

  async function handleDeleteColor(color: ClimbScanColor) {
    if (!slug || !scan) return;

    // **Nothing to delete.** A proposal was never written down: turning one
    // away is putting it out of sight, and the holds it was made of stay on the
    // wall for the next climb.
    if (isProposal(color.id)) {
      setDismissed((prev) => [ ...prev, color.id ]);
      if (selectedColorId === color.id) selectColor(null);
      return;
    }

    setDeletingColorId(color.id);
    try {
      await climbScansService.deleteColor(slug, scan.id, color.id);
      setScan((prev) => (prev ? { ...prev, colors: prev.colors.filter((c) => c.id !== color.id) } : prev));
      if (selectedColorId === color.id) selectColor(null);
      loadScans();
    } catch {
      toast({ title: t('gymAdmin.scans.deleteError'), variant: 'destructive' });
    } finally {
      setDeletingColorId(null);
    }
  }

  /**
   * A climb made of what was lit on the wall — **and still nothing in the
   * database**.
   *
   * It used to create a `ClimbScanColor` the moment the button was pressed,
   * which is exactly the half-made climb a scan should not leave behind. The
   * selection becomes a proposal like the detector's own, and reaches the
   * database only when the form beside it is saved.
   */
  function handleAddColorWithHolds(holds: Hold[]) {
    if (!scan) return;

    const id = `${PROPOSAL_PREFIX}custom:${customProposals.length}`;
    const proposal: ClimbScanColor = {
      id,
      climb_scan_id: scan.id,
      color_name: null,
      hex_colors: [],
      hold_count: holds.length,
      detected_holds: [],
      climb_name: null,
      status: 'pending',
      climb: null,
      created_at: scan.created_at,
      updated_at: scan.updated_at,
    };

    setCustomProposals((prev) => [ ...prev, proposal ]);
    // **The climb opens on the holds that were lit.** They were chosen on the
    // wall before it existed, which is the whole point of the picker: the work
    // is choosing, and the form is what is left afterwards.
    setDrafts((prev) => ({ ...prev, [id]: draftFromColor(proposal, scan) }));
    setMarks((prev) => ({ ...prev, [id]: { ...NO_MARKS, holds } }));
    seededColorIdsRef.current.add(id);
    selectColor(id);
  }

  /**
   * Look at the wall again, or at one of its colours. The reason the dialog
   * collected is the only thing the model is told about the last answer — its
   * own boxes are deliberately not fed back, since a wrong answer handed back
   * is something a model anchors on rather than learns from.
   */
  async function handleRescan(
    reason: string | null,
    sensitivity: ScanSensitivity | null,
    wholePhoto: boolean,
    chosen: ScanDetector | null
  ) {
    if (!slug || !rescanTarget) return;

    const { scanId, colorId } = rescanTarget;
    setRescanning(true);
    try {
      if (colorId) {
        const updated = await climbScansService.rescanColor(slug, scanId, colorId, reason);
        setScan((prev) =>
          prev ? { ...prev, colors: prev.colors.map((c) => (c.id === colorId ? updated : c)) } : prev
        );
        // The job runs in the background, so what came back is the colour as it
        // still is. Watch it until the new detection lands.
        setAwaitedColor({ id: colorId, since: updated.updated_at });
        toast({ title: t('gymAdmin.scans.rescan.queued') });
      } else {
        await startScan(scanId, reason, sensitivity, wholePhoto, chosen);
      }
      setRescanTarget(null);
    } catch {
      toast({ title: t('gymAdmin.scans.retryError'), variant: 'destructive' });
    } finally {
      setRescanning(false);
    }
  }

  /**
   * Start the analysis of one wall. The press the upload stopped making: a
   * photo waits at `pending` until the hourly batch comes round or this is
   * pressed, and pressing it is what a setter who wants this wall *now* does —
   * at the price of waiting out a cold boot.
   */
  async function startScan(
    scanId: string,
    reason: string | null = null,
    sensitivity: ScanSensitivity | null = null,
    wholePhoto = false,
    chosen: ScanDetector | null = null
  ) {
    if (!slug) return;

    const updated = await climbScansService.startScan(slug, scanId, reason, sensitivity, wholePhoto, chosen);
    // Only if it is the one on screen — any row can ask, including rows for
    // scans nobody has opened.
    if (updated.id === scan?.id) {
      setScan(updated);
      // Its circles are about to be replaced, so the seeding guard has to let
      // the new ones through.
      seededColorIdsRef.current = new Set();
    }
    // The list carries the status badge, which has just changed.
    loadScans();
  }

  /**
   * A press on a row's or the photo's Scan button. **It always asks first.**
   *
   * Every press spends a model call and wakes a GPU up, and the two things
   * worth settling before one — why the last answer was wrong, and how hard to
   * look this time — are the whole of what the dialog collects. It used to open
   * only for a wall with saved climbs on it, which read the rows for "has this
   * been looked at" and so skipped the question on every wall whose holds were
   * still proposals: the walls with most to say about the last answer were the
   * ones that could not be told anything. The dialog puts whichever question
   * the detector can hear, in words that fit a first look where there has been
   * none — see RescanDialog.
   */
  function handleScanPress(scanId: string) {
    setRescanTarget({ scanId, colorId: null });
  }

  async function handleUpload(signedId: string, climbType: 'boulder' | 'route') {
    if (!slug) return;
    try {
      const created = await climbScansService.createScan(slug, {
        climb_type: climbType,
        image_signed_id: signedId,
      });
      setScans((prev) => [created, ...prev]);
      selectScan(created.id);
    } catch {
      toast({ title: t('gymAdmin.scans.uploadError'), variant: 'destructive' });
    }
  }


  if (gymLoading) return <Skeleton className="h-96 w-full" />;

  // `pending` stopped meaning *queued* when the upload stopped starting
  // anything: a waiting photo is one nobody has asked about yet, and that is a
  // button rather than a spinner. Only `processing` is a wall being looked at —
  // unless it is stalled, whose worker died mid-run and which nobody is
  // looking at any more.
  const waiting = scan?.status === 'pending';
  const stalled = scan?.status === 'processing' && scan.stalled;
  const running = scan?.status === 'processing' && !scan.stalled;
  // No way is there an answer to show yet, which is what the columns below key
  // the "nothing found" line off.
  const unanswered = waiting || running || stalled;
  // The wall's other climbs, drawn on the photo whichever way it is being
  // shown — and pressable, so the photo is a way into the list as well as a
  // view of it.
  const previews = scan ? previewsForScan(scan, selectedColorId) : [];


  // A climb is not one until the photo says where it starts, what it is climbed
  // on and where it tops out — or, for a route, until its line has two points
  // to run between. The save waits on that as much as on the fields.
  const isRoute = scan?.climb_type === 'route';
  const missingRoles = isRoute ? [] : missingPhotoHoldRoles(selectedMarks?.holds ?? []);
  const lineIncomplete = !!isRoute && !isDrawableLine(selectedMarks?.line ?? null);

  // The list is a list of work left, so what is done sinks past the button that
  // adds more. Both halves keep the analysis's own order.

  const pendingColors = allColors.filter((color) => color.status !== 'completed');
  const publishedColors = allColors.filter((color) => color.status === 'completed');

  function renderColorCard(color: ClimbScanColor) {
    return (
      <ScanColorCard
        key={color.id}
        color={color}
        draft={drafts[color.id] ?? draftFromColor(color, scan!)}
        selected={selectedColorId === color.id}
        onSelect={() => selectColor(selectedColorId === color.id ? null : color.id)}
        markCount={
          isRoute ? marks[color.id]?.line?.points.length ?? 0 : marks[color.id]?.holds.length ?? 0
        }
      />
    );
  }

  // What the photo and the controls columns need from the page: their shared
  // width, the banner above the photo, and the scan's settings under the
  // controls. The editors carry it through without reading it.
  const chrome: ScanEditorChrome = {
    panelWidth,
    onPanelWidthChange: setPanelWidth,
    // The circuit in the order it is climbed, at the top of the controls column,
    // where it can be read against the photo beside it. Only for a climb that is
    // one — switched on from the corner of the picture.
    circuit:
      circuit && selectedColor ? (
        <CircuitList
          holds={circuitHolds}
          onChange={setHolds}
          roleOf={photoHoldRole}
          selected={holdEditor.selected}
          onSelect={(index) => holdEditor.toggleSelected(index, false)}
          forcedHands={forcedHands}
          onForcedHandsChange={setForcedHands}
          onStartingHandChange={setStartingHand}
        />
      ) : null,
    // What the wall is waiting for, above the picture it is waiting about. The
    // warning goes with the button: a scan started by hand wakes a GPU up, and
    // nine minutes of silence reads as broken unless the panel says so
    // beforehand. Everybody else is told what is going to happen anyway, since
    // "waiting" with no button looks like nothing happening.
    notice:
      scan && (unanswered || scan.status === 'failed') ? (
        <div className="space-y-2 rounded-md border p-3">
          {waiting && (
            <>
              <p className="text-sm text-muted-foreground">{t('climbScan.waitingMessage')}</p>
              {canRescan && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    disabled={rescanning}
                    onClick={() => handleScanPress(scan.id)}
                    data-testid="scan-now"
                  >
                    <RefreshCw className={`size-4 ${rescanning ? 'animate-spin' : ''}`} aria-hidden />
                    {t('climbScan.scanNow')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('climbScan.firstScanHint')}</p>
                </>
              )}
            </>
          )}
          {running && (
            <p className="animate-pulse text-center text-sm text-muted-foreground">
              {t('climbScan.processingMessage')}
            </p>
          )}
          {stalled && (
            <p className="text-sm text-muted-foreground">{t('climbScan.stalledMessage')}</p>
          )}
          {scan.status === 'failed' && (
            <p className="text-sm text-destructive">{t('climbScan.failedMessage')}</p>
          )}
        </div>
      ) : null,
    // What is done with the climb, at the head of the column — where the strip
    // of drawing tools used to be, and where the save can be reached without
    // scrolling the form under it to its end.
    actions:
      scan && selectedColor ? (
        <ScanClimbActions
          onSave={() => requestSaveColor(selectedColor)}
          onDelete={() => handleDeleteColor(selectedColor)}
          saving={savingColorId === selectedColor.id}
          deleting={deletingColorId === selectedColor.id}
          // A climb is what it is graded and what is on the wall, so the marks
          // gate the save alongside the fields. What is still wanted is said on
          // the photo, which is where it is answered (see MissingHint).
          ready={
            !!(drafts[selectedColor.id] ?? draftFromColor(selectedColor, scan)).gradeId &&
            missingRoles.length === 0 &&
            !lineIncomplete
          }
        />
      ) : null,
    // The panel says one thing at a time: the climb being worked on, or — when
    // none is open — what the whole scan is credited to. Both at once would be
    // asking about the wall and about one of its climbs in the same breath.
    tools:
      scan && selectedColor ? (
        <ScanClimbForm
          color={selectedColor}
          draft={drafts[selectedColor.id] ?? draftFromColor(selectedColor, scan)}
          onDraftChange={(patch) => patchDraft(selectedColor.id, patch)}
          gradingSystem={gradingSystem}
          gymSlug={slug ?? ''}
          locationId={scan.location?.id ?? null}
          fieldErrors={colorErrors[selectedColor.id] ?? {}}
          onDrawName={nameSuggestions.length > 0 ? () => drawName(selectedColor.id) : undefined}
          lineIncomplete={lineIncomplete}
        />
      ) : scan ? (
        <>
          {/* The wall itself — which of its holds it offers, and the ones it
              missed — above what it is credited to. Only on a wall that has
              holds to offer, and only with no climb open. */}
          {wallTools.tools && <ScanWallTools {...wallTools.tools} />}
          <ScanDefaults scan={scan} zones={zones} saving={savingDefaults} onSave={handleSaveDefaults} />
        </>
      ) : null,
  };

  return (
    // No heading: the sidebar already says which section this is, and the four
    // columns need every row of the window they can get.
    <div className="flex h-[calc(100svh-5rem)] flex-col gap-3">
      {/* Four columns, left to right in the order the work is done: which scan,
          which climb on it, the photo, and the tools that draw on it. Each
          scrolls on its own, so a long list of climbs never pushes the photo
          off the top.

          The photo is the elastic column (`1fr`); the climbs list and the panel
          are the widths that were dragged out for them, so every pixel either
          gives back goes to the picture. Desktop-first, but a setter reads this
          at the wall on a phone: below `lg` it all stacks and the dividers,
          having nothing to divide, are hidden. */}
      <div
        className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:gap-0 lg:[grid-template-columns:minmax(14rem,18rem)_var(--climbs-width)_0.75rem_minmax(0,1fr)_0.75rem_var(--panel-width)]"
        style={
          {
            '--climbs-width': `${climbsWidth}px`,
            '--panel-width': `${panelWidth}px`,
          } as React.CSSProperties
        }
      >
        <section
          className="min-h-0 overflow-y-auto rounded-md border lg:mr-3"
          aria-label={t('gymAdmin.scans.listLabel')}
        >
          {/* Uploading a wall is the first thing done here and the list is
              where it lands, so it sits at the head of the list rather than in
              a bar of its own. */}
          <div className="border-b p-2">
            <ScanUploadDialog onUpload={handleUpload} />
          </div>

          <ScanListColumn
            scans={scans}
            selectedId={selectedScanId}
            onSelect={selectScan}
            loading={listLoading}
            onScan={canRescan ? handleScanPress : undefined}
          />
        </section>

        <section
          className="min-h-0 overflow-y-auto rounded-md border p-2"
          aria-label={t('gymAdmin.scans.climbsLabel')}
          data-testid="scan-climbs-column"
        >
          {!scan ? (
            <p className="p-2 text-sm text-muted-foreground">{t('gymAdmin.scans.selectScan')}</p>
          ) : (
            <div className="space-y-2">
              {allColors.length === 0 && !unanswered && (
                <p className="p-2 text-sm text-muted-foreground">{t('climbScan.noColors')}</p>
              )}

              {/* Still to do, in the order the analysis found them. */}
              {pendingColors.map(renderColorCard)}

              {scan.status === 'ready' && (
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => handleAddColorWithHolds(NO_HOLDS)}>
                  {t('gymAdmin.scans.addColor')}
                </Button>
              )}

              {/* Published, and so out of the way: the list is a list of work
                  left, and a wall's finished climbs would otherwise sit between
                  the setter and the ones still wanting doing. Below the button
                  rather than hidden — they are still the wall's climbs, and
                  still worth opening. */}
              {publishedColors.map(renderColorCard)}
            </div>
          )}
        </section>

        <ColumnResizer
          width={climbsWidth}
          onWidthChange={setClimbsWidth}
          min={MIN_CLIMBS_WIDTH}
          max={MAX_CLIMBS_WIDTH}
          label={t('gymAdmin.scans.resizeColumns')}
          side="left"
        />

        {/* The photo and the panel are two of this grid's columns, so whatever
            fills them has to be a pair of siblings here — hence ScanEditorLayout
            rendering a fragment, and every branch below producing one. */}
        {scanLoading ? (
          <PlaceholderColumns>
            <Skeleton className="h-96 w-full" />
          </PlaceholderColumns>
        ) : !scan || !photoUrl ? (
          <PlaceholderColumns>
            <p className="text-sm text-muted-foreground">{t('gymAdmin.scans.selectScan')}</p>
          </PlaceholderColumns>
        ) : !naturalSize ? (
          // Measured per picture, and everything drawn on it is expressed
          // against that size — the frame the member app redraws the marks in.
          <PlaceholderColumns>
            <img
              src={photoUrl}
              alt={t('climbScan.scanImage')}
              className="w-full rounded-md border"
              onLoad={(event) =>
                setPhotoSize({
                  // The URL this <img> was given, not the one it resolved to:
                  // the measurement is looked up by the picture it belongs to.
                  url: photoUrl,
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
            />
          </PlaceholderColumns>
        ) : selectedColor ? (
          scan.climb_type === 'route' ? (
            <ScanLineEditor
              imageUrl={photoUrl}
              naturalWidth={naturalSize.width}
              naturalHeight={naturalSize.height}
              line={selectedMarks?.line ?? null}
              onChange={setLine}
              chrome={chrome}
            />
          ) : (
            <ScanHoldEditor
              imageUrl={photoUrl}
              editor={holdEditor}
              missingRoles={missingRoles}
              circuit={circuit}
              onCircuitChange={setCircuit}
              // The hands as the fourth column reads them, painted onto the
              // circles too — off circuitHolds rather than the marks, which a
              // circle just drawn or moved has no hand on yet.
              handsOf={(index) => {
                const hold = circuitHolds[index];
                return hold ? photoHandsOf(hold) : [];
              }}
              chrome={chrome}
            />
          )
        ) : (placeholders.length > 0 || shown.length < (scan?.detected_holds.length ?? 0)) &&
          photoUrl &&
          naturalSize ? (
          // The wall as the scan left it: every hold on it, waiting to be made
          // into climbs. This is the page's resting state now — it used to be a
          // plain photograph with nothing to do on it.
          <ScanPlaceholderPicker
            key={wallTools.picker.key}
            imageUrl={photoUrl}
            naturalWidth={naturalSize.width}
            naturalHeight={naturalSize.height}
            placeholders={wallTools.picker.placeholders}
            maybe={wallTools.picker.maybe}
            onChosenChange={wallTools.picker.onChosenChange}
            wand={wallTools.picker.wand}
            onAdd={handleAddColorWithHolds}
            adding={false}
            previews={previews}
            onSelectColor={selectColor}
            chrome={chrome}
            areaScan={wallTools.picker.areaScan}
          />
        ) : (
          // A scan with nothing found on it, or one still being looked at.
          <ScanEditorLayout
            canvas={
              <div className="relative">
                <img src={photoUrl} alt={t('climbScan.scanImage')} className="block w-full rounded-md border" />
                <ScanColorPreviews previews={previews} onSelect={selectColor} />
              </div>
            }
            {...chrome}
          />
        )}
      </div>

      {/* Zoomed in at the moment of saving: is the framing the climb, or was it
          just a closer look? Asked with both answers shown, drawing and all. */}
      <CropChoiceDialog
        photo={
          photoUrl && naturalSize
            ? {
                url: photoUrl,
                naturalWidth: naturalSize.width,
                naturalHeight: naturalSize.height,
                file: null,
              }
            : null
        }
        crop={pendingCrop?.rect ?? null}
        holds={selectedMarks?.holds ?? []}
        line={selectedMarks?.line ?? null}
        onChoose={(rect) => {
          const color = allColors.find((candidate) => candidate.id === pendingCrop?.colorId);
          if (color) void handleSaveColor(color, rect);
        }}
        onDismiss={() => setPendingCrop(null)}
      />

      <RescanDialog
        open={rescanTarget !== null}
        onOpenChange={(open) => !open && setRescanTarget(null)}
        scope={rescanTarget?.colorId ? 'color' : 'scan'}
        // The gym's default answers, unless the setter picks the precise
        // look — offered on a gym with both models, and already picked on a
        // wall SAM 3 answered.
        detector={rescanScan?.detector ?? 'gemini'}
        precise={!!rescanScan?.precise_scan}
        preciseByDefault={rescanScan?.answered_by === 'sam'}
        // A colour only exists on a wall that answered, so the scope settles it
        // there; for a whole wall it is the holds and the rows together, since
        // the holds are written down by the analysis and the rows only by a
        // setter accepting one of its proposals.
        answered={!!rescanTarget?.colorId || !!(rescanScan && hasScanAnswer(rescanScan))}
        onConfirm={handleRescan}
        running={rescanning}
      />
    </div>
  );
}

/**
 * Stands in for the photo and panel columns before there is anything to edit.
 *
 * A fragment of two cells rather than one spanning both: the grid's columns are
 * fixed, and a single cell would leave the panel's column empty and the layout
 * jumping as soon as the editor arrives.
 */
function PlaceholderColumns({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <>
      <section
        className="min-h-0 overflow-y-auto rounded-md border p-3"
        aria-label={t('gymAdmin.scans.imageLabel')}
        data-testid="scan-canvas-column"
      >
        {children}
      </section>
      {/* No divider: there is nothing to size yet. It keeps the column so the
          grid's shape does not change under the editor when it loads. */}
      <div aria-hidden />
      <aside
        className="hidden min-h-0 overflow-y-auto rounded-md border p-3 lg:block lg:w-[var(--panel-width)]"
        aria-label={t('gymAdmin.scans.panelLabel')}
        data-testid="scan-editor-panel"
      />
    </>
  );
}

